/**
 * Module Permissions Context
 *
 * Fetches module permission configs from the backend (single source of truth)
 * and provides them to the frontend. This means permission strings are defined
 * ONCE in the backend and the frontend resolves them dynamically.
 *
 * Usage:
 *   const { getPermissions } = useModulePermissions();
 *   const viewPerms = getPermissions('clients', 'view');
 *   // → ['ADMIN', 'TENANT_ADMIN', 'HEALTHCARE_ROLE']
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useAuth } from './AuthContext';

// ==========================================
// GraphQL Query
// ==========================================

const GET_MODULE_PERMISSIONS = gql`
  query GetModulePermissions {
    modulePermissions {
      moduleId
      view
      create
      edit
      delete
      publish
      financial
      review
      send
      admin
      addressBook
      share
      permissions
      merge
      self
    }
  }
`;

// ==========================================
// Types
// ==========================================

interface ModulePermissionConfig {
  moduleId: string;
  view: string[];
  create: string[];
  edit: string[];
  delete: string[];
  publish?: string[];
  financial?: string[];
  review?: string[];
  send?: string[];
  admin?: string[];
  addressBook?: string[];
  share?: string[];
  permissions?: string[];
  merge?: string[];
  self?: string[];
}

interface ModulePermissionsContextType {
  /** Map of moduleId → permission config from backend */
  permissions: Record<string, ModulePermissionConfig>;
  /** Get permission strings for a module + level (e.g. 'clients', 'view') */
  getPermissions: (moduleId: string, level: string) => string[];
  /** Whether the permissions are still loading */
  loading: boolean;
}

// ==========================================
// Context
// ==========================================

const ModulePermissionsContext = createContext<ModulePermissionsContextType>({
  permissions: {},
  getPermissions: () => [],
  loading: true,
});

export function useModulePermissions() {
  return useContext(ModulePermissionsContext);
}

// ==========================================
// Provider
// ==========================================

export function ModulePermissionsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  const { data, loading } = useQuery(GET_MODULE_PERMISSIONS, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-first',
  });

  const value = useMemo(() => {
    const permissionsMap: Record<string, ModulePermissionConfig> = {};

    if (data?.modulePermissions) {
      for (const config of data.modulePermissions) {
        permissionsMap[config.moduleId] = config;
      }
    }

    return {
      permissions: permissionsMap,
      getPermissions: (moduleId: string, level: string): string[] => {
        const config = permissionsMap[moduleId];
        if (!config) return [];
        return (config as any)[level] || [];
      },
      loading: isAuthenticated ? loading : false,
    };
  }, [data, loading, isAuthenticated]);

  return (
    <ModulePermissionsContext.Provider value={value}>
      {children}
    </ModulePermissionsContext.Provider>
  );
}
